import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Droplets, TestTube2, FlaskConical, Beaker,
  ShieldCheck, Clock3, MapPin, FileText, Sparkles,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FAQ } from "@/components/FAQ";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import {
  SchemaJsonLd,
  serviceSchema,
  productSchema,
  faqSchema,
  localBusinessSchema,
} from "@/lib/seo";
import { TESTS } from "@/lib/siteData";
import { TEST_PARAMETERS } from "@/lib/testParameters";
import { usePublicPricing } from "@/lib/usePublicPricing";

/**
 * Shared template for the three dedicated test-package landing pages.
 *
 * Each call site sets:
 *   - `testId` — "basic" | "tank" | "bore"  (looks up `TESTS` + `TEST_PARAMETERS`)
 *   - `seo`    — { title, description, urlPath }
 *   - `intro`  — Hero copy (h1 / lede / icon / bestFor bullets)
 *   - `whoFor` — Long-form "Who this test is for" paragraph
 *   - `faqs`   — Page-specific FAQ pairs
 *   - `sections`   — (optional) extra informational blocks rendered
 *     between the parameter grid and the FAQ: { title, body, items[] }
 *   - `learnLinks` — (optional) contextual outbound links to Knowledge
 *     Hub / article content, appended to the "Also explore" sidebar
 */
const ICONS = { basic: Droplets, tank: TestTube2, bore: FlaskConical };

/**
 * Feb 2026 — hero banner per test package. User-supplied illustrated
 * banners already carry the AWARE wordmark, product number, tagline
 * and analysis-category icons, so they replace the standalone <h1>
 * text headline visually. The semantic <h1> stays in the DOM as
 * `sr-only` so SEO + assistive tech still see the exact product name.
 * Native aspect ratio ~ 1920 × 343 (~5.6:1).
 */
const TEST_BANNER_URL = {
  basic: "/img/v2/aware-4-point-banner.webp",
  tank:  "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/kt8esog0_AWARE_Tank_360_Banner.png",
  bore:  "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/y2665fat_AWARE_Bore_360_Banner.png",
};

export function TestPackagePage({ testId, seo, intro, whoFor, faqs, sections, learnLinks }) {
  const test = TESTS.find((t) => t.id === testId);
  const Icon = ICONS[testId] || Beaker;
  const params = TEST_PARAMETERS[testId];
  const url = `${SEO_DEFAULTS.baseUrl}${seo.urlPath}`;
  const { enabled: discountEnabled, pct: discountPct, showStrikethrough, priceFor } = usePublicPricing();
  const live = priceFor(testId) || {};
  const price     = live.effective ?? (discountEnabled ? test.online_prepaid : test.retail);
  const retail    = live.retail    ?? test.retail;
  const showStrike = discountEnabled && showStrikethrough && retail > price;
  const showPill   = discountEnabled && discountPct > 0;
  const primaryCtaLabel = showPill ? `Book this test & Save ${discountPct}%` : "Book this test";
  const finalCtaLabel   = showPill ? `Book online & save ${discountPct}%`    : "Book online";
  const finalCtaBlurb   = showPill
    ? `Book your ${test.name.toLowerCase()} online — receive your sample kit instructions immediately and save ${discountPct}% on retail pricing.`
    : `Book your ${test.name.toLowerCase()} online — receive your sample kit instructions immediately.`;

  useSEO({
    title: seo.title,
    description: seo.description,
    url,
  });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* JSON-LD: LocalBusiness + Service + Product + FAQ */}
      <SchemaJsonLd data={localBusinessSchema()} id="jsonld-localbusiness" />
      <SchemaJsonLd
        data={serviceSchema(test.name,
          `${intro.lede} Available Australia-wide with local drop-off at our Noosaville laboratory.`,
          "Australia")}
        id="jsonld-service"
      />
      <SchemaJsonLd
        data={productSchema({
          name: test.name,
          price: price,
          description: intro.lede,
        })}
        id="jsonld-product"
      />
      {faqs?.length > 0 && (
        <SchemaJsonLd data={faqSchema(faqs)} id="jsonld-faq" />
      )}

      <Header />

      <main>
        {/* ============ HERO ============ */}
        <section className="pt-28 lg:pt-36 pb-12 lg:pb-16 bg-gradient-to-br from-slate-50 via-white to-cyan-50/40 border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs items={[
              { name: "Water Tests", url: "/" },
              { name: test.name },
            ]} />

            {/* ═══════ HERO BANNER (Feb 2026) ═══════
                Full-width illustrated banner containing the AWARE
                wordmark, product number (4-POINT / TANK 360 / BORE
                360), tagline, category icons and supporting scene.
                Replaces the standalone H1 headline visually — the
                banner IS the headline. The semantic <h1> lives on as
                sr-only so screen readers + search engines still see
                the exact product name text. */}
            {TEST_BANNER_URL[testId] && (
              <div className="mt-6 -mx-4 sm:-mx-6 lg:-mx-8" data-testid={`pkg-${testId}-hero-banner`}>
                <img
                  src={TEST_BANNER_URL[testId]}
                  alt={`${test.name} — ${intro.h1} banner`}
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  width="1920"
                  height="343"
                  className="w-full h-auto object-cover"
                />
              </div>
            )}

            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start mt-6">
              <div className="lg:col-span-7">
                {/* Category chip stays for context above the lede. */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-5">
                  <Icon className="w-4 h-4 text-cyan-600" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700">
                    {test.category}
                  </span>
                </div>

                {/* Feb 2026 — the banner above IS the visual H1. Keep
                    the semantic <h1> for a11y + SEO, hidden from
                    sighted users to avoid duplicating the banner. */}
                <h1 className={TEST_BANNER_URL[testId] ? "sr-only" : "text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight text-slate-900"}>
                  {intro.h1}
                </h1>

                <p className={`text-lg text-slate-600 leading-relaxed max-w-2xl ${TEST_BANNER_URL[testId] ? "mt-0" : "mt-6"}`}>
                  {intro.lede}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to="/book-water-test"
                    data-testid={`pkg-${testId}-book-btn`}
                    className="btn-aqua inline-flex items-center gap-1.5 px-6 py-3 rounded-full text-sm font-semibold"
                  >
                    {primaryCtaLabel} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid={`pkg-${testId}-contact-link`}
                    className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-sm font-semibold border border-slate-200 hover:border-cyan-400 hover:text-cyan-700 transition-colors"
                  >
                    Ask a question
                  </Link>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-cyan-600" /> Sample drop-off in Noosaville</span>
                  <span className="inline-flex items-center gap-1.5"><Clock3 className="w-4 h-4 text-cyan-600" /> Reports usually in 1–3 days</span>
                  <span className="inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-cyan-600" /> Plain-English summary report</span>
                </div>
              </div>

              {/* Right pricing card */}
              <aside className="lg:col-span-5">
                <div className="rounded-3xl bg-white border border-slate-200 shadow-xl p-6 lg:p-7 sticky top-28">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl lg:text-4xl font-display font-bold">${price}</span>
                    {showStrike && (
                      <span className="text-base text-slate-400 line-through">${retail}</span>
                    )}
                    {showPill && (
                      <span className="ml-auto text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                        Save {discountPct}%
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {showPill ? "Online pre-paid price" : "Standard price"}
                  </p>

                  <ul className="mt-5 space-y-2.5">
                    {test.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/book-water-test"
                    data-testid={`pkg-${testId}-sidebar-book-btn`}
                    className="mt-6 btn-aqua w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-sm font-semibold"
                  >
                    {showPill ? <>Book online &amp; save <ArrowRight className="w-4 h-4" /></> : <>Book online <ArrowRight className="w-4 h-4" /></>}
                  </Link>
                  <p className="mt-3 text-[11px] text-slate-500 text-center">
                    Or call <a href="tel:+61753273473" className="font-semibold text-slate-700 hover:text-cyan-700">(07) 5327 3473</a>
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* ============ WHO IT'S FOR ============ */}
        <section className="py-14 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <h2 className="text-3xl font-display font-bold mb-4">Who this test is for</h2>
              <p className="text-base text-slate-600 leading-relaxed">{whoFor}</p>

              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {test.bestFor.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-xl p-3.5">
                    <ShieldCheck className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-cyan-600" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700">Also explore</span>
                </div>
                <ul className="space-y-2.5 text-sm">
                  {testId !== "basic" && (
                    <li>
                      <Link to="/aware-4-point" className="font-medium text-slate-800 hover:text-cyan-700 inline-flex items-center gap-1">
                        AWARE 4-Point <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </li>
                  )}
                  {testId !== "tank" && (
                    <li>
                      <Link to="/aware-tank-360" className="font-medium text-slate-800 hover:text-cyan-700 inline-flex items-center gap-1">
                        AWARE Tank 360 <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </li>
                  )}
                  {testId !== "bore" && (
                    <li>
                      <Link to="/aware-bore-360" className="font-medium text-slate-800 hover:text-cyan-700 inline-flex items-center gap-1">
                        AWARE Bore 360 <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link to="/locations" className="font-medium text-slate-800 hover:text-cyan-700 inline-flex items-center gap-1">
                      Local drop-off &amp; service areas <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </li>
                  <li>
                    <Link to="/knowledge-hub" className="font-medium text-slate-800 hover:text-cyan-700 inline-flex items-center gap-1">
                      Knowledge Hub &amp; learning <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </li>
                  {(learnLinks || []).map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        data-testid={`pkg-${testId}-learn-${l.to.split("/").pop()}`}
                        className="font-medium text-slate-800 hover:text-cyan-700 inline-flex items-center gap-1"
                      >
                        {l.label} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>

        {/* ============ WHAT WE TEST FOR ============ */}
        {params && (
          <section className="py-14 lg:py-20 bg-slate-50/60 border-y border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-2xl mb-10">
                <h2 className="text-3xl font-display font-bold mb-3">What we analyse</h2>
                <p className="text-base text-slate-600 leading-relaxed">
                  The full parameter list included with the {test.name}.
                  Each parameter is tested using recognised laboratory methodologies.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(params.groups).map(([group, list]) => (
                  <div key={group} className="rounded-2xl bg-white border border-slate-200 p-5">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-cyan-700 mb-3">{group}</h3>
                    <ul className="space-y-2 text-sm text-slate-700">
                      {(list || []).map((p) => (
                        <li key={p} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-emerald-600 mt-1 flex-shrink-0" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ============ EXTRA INFORMATIONAL SECTIONS ============
            Page-specific educational blocks (Jun 2026 — added for the
            Tank 360 rainwater-testing brief). Rendered only when a
            call site passes `sections`. */}
        {(sections || []).length > 0 && (
          <section className="py-14 lg:py-20" data-testid={`pkg-${testId}-sections`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16">
              {sections.map((s, i) => (
                <div key={s.title} data-testid={`pkg-${testId}-section-${i}`}>
                  <h2 className="text-2xl lg:text-3xl font-display font-bold mb-4">{s.title}</h2>
                  <p className="text-base text-slate-600 leading-relaxed">{s.body}</p>
                  <ul className="mt-5 space-y-2.5">
                    {(s.items || []).map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {s.footnote && (
                    <p className="mt-5 text-sm text-slate-500 leading-relaxed">{s.footnote}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ============ FAQ ============ */}
        {faqs?.length > 0 && (
          <FAQ
            qas={faqs}
            subtitle="FAQ"
            title={`About the ${test.name}`}
            testIdPrefix={`pkg-${testId}-faq`}
          />
        )}

        {/* ============ FINAL CTA ============ */}
        <section className="py-16 lg:py-20 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              Ready to test your water?
            </h2>
            <p className="text-base sm:text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              {finalCtaBlurb}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/book-water-test"
                data-testid={`pkg-${testId}-final-book-btn`}
                className="btn-aqua inline-flex items-center gap-1.5 px-7 py-3.5 rounded-full text-sm font-semibold"
              >
                {finalCtaLabel} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full text-sm font-semibold border border-white/20 hover:bg-white/10 transition-colors text-white"
              >
                Contact the lab
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default TestPackagePage;
